/**
 * Donations RTK Contracts — Package-Owned Semantic Layer
 *
 * MODULE_ID, ROUTE_IDS, QUERY/MUTATION_CONTRACTS with Zod schema references.
 * Sources of truth for semantic route accountability and RTK glue wiring.
 *
 * Pattern: packages/rtk-abstractions/src/contracts/payments-contracts.ts
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/implementation-plan.md
 *
 * Locked Decision #8: RTK glue = package-owned. Never wire app-local RTK
 * contract wrappers directly in apps/ystnj.
 */

import { z } from 'zod';
import {
  CampaignCalendarResponseSchema,
  TenantParnasHayomConfigSchema,
  FamilyDonationHistoryListResponseSchema,
  PendingDedicationsListResponseSchema,
  DailyHonoreesResponseSchema,
  LedgerListResponseSchema,
  DonationCampaignListResponseSchema,
  DonationCampaignSchema,
  DonationSchema,
} from '@triedge/domain-core';
import {
  toAppSyncMutationName,
  toAppSyncOperationName,
  toRtkGlueModuleId,
  toSemanticRouteId,
  type AppSyncMutationName,
  type AppSyncOperationName,
  type RtkGlueModuleId,
  type SemanticRouteId,
} from './semantic-types';

// ---------------------------------------------------------------------------
// Contract Types
// ---------------------------------------------------------------------------

type DonationsQueryContract = {
  operationName: AppSyncOperationName;
  routeId: SemanticRouteId;
  outputSchema: z.ZodTypeAny;
};

type DonationsMutationContract = {
  mutationName: AppSyncMutationName;
  routeId: SemanticRouteId;
  inputSchema: z.ZodTypeAny;
  outputSchema: z.ZodTypeAny;
};

const DonationsCampaignMutationInputSchema = DonationCampaignSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

const DonationsCampaignUpdateInputSchema = DonationsCampaignMutationInputSchema.extend({
  campaignId: z.string().uuid(),
}).strict();

const DonationsDedicationActionInputSchema = z.object({
  donationId: z.string().uuid(),
}).strict();

const DonationsDedicationActionWithReasonInputSchema = DonationsDedicationActionInputSchema.extend({
  reason: z.string().optional(),
}).strict();

const DonationsReceiptResultSchema = z.object({
  success: z.boolean(),
}).strict();

// ---------------------------------------------------------------------------
// Module + Route IDs
// ---------------------------------------------------------------------------

export const DONATIONS_MODULE_ID: RtkGlueModuleId = toRtkGlueModuleId('donations');

export const DONATIONS_ROUTE_IDS = Object.freeze({
  donationsParentDashboard: toSemanticRouteId('donations-parent-dashboard'),
  parnesHayomParentLanding: toSemanticRouteId('parnes-hayom-parent-landing'),
  parnesHayomParentCheckout: toSemanticRouteId('parnes-hayom-parent-checkout'),
  parnesHayomParentSuccess: toSemanticRouteId('parnes-hayom-parent-success'),
  parnesHayomParentHistory: toSemanticRouteId('parnes-hayom-parent-history'),
  donationsSchoolDashboard: toSemanticRouteId('donations-school-dashboard'),
  donationsSchoolModeration: toSemanticRouteId('donations-school-moderation'),
  donationsSchoolLedger: toSemanticRouteId('donations-school-ledger'),
  donationsTeacherHonorees: toSemanticRouteId('donations-teacher-honorees'),
});

// ---------------------------------------------------------------------------
// Query Contracts
// ---------------------------------------------------------------------------

export const DONATIONS_QUERY_CONTRACTS = Object.freeze({
  getDonationsCampaignCalendar: {
    operationName: toAppSyncOperationName('getDonationsCampaignCalendar'),
    routeId: DONATIONS_ROUTE_IDS.donationsParentDashboard,
    outputSchema: CampaignCalendarResponseSchema,
  },
  getDonationsTenantConfig: {
    operationName: toAppSyncOperationName('getDonationsTenantConfig'),
    routeId: DONATIONS_ROUTE_IDS.parnesHayomParentLanding,
    outputSchema: TenantParnasHayomConfigSchema,
  },
  listDonationsFamilyHistory: {
    operationName: toAppSyncOperationName('listDonationsFamilyHistory'),
    routeId: DONATIONS_ROUTE_IDS.parnesHayomParentHistory,
    outputSchema: FamilyDonationHistoryListResponseSchema,
  },
  listDonationsPendingDedications: {
    operationName: toAppSyncOperationName('listDonationsPendingDedications'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolModeration,
    outputSchema: PendingDedicationsListResponseSchema,
  },
  getDonationsDailyHonorees: {
    operationName: toAppSyncOperationName('getDonationsDailyHonorees'),
    routeId: DONATIONS_ROUTE_IDS.donationsTeacherHonorees,
    outputSchema: DailyHonoreesResponseSchema,
  },
  getDonationsLedger: {
    operationName: toAppSyncOperationName('getDonationsLedger'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolLedger,
    outputSchema: LedgerListResponseSchema,
  },
  getDonationsCampaignList: {
    operationName: toAppSyncOperationName('getDonationsCampaignList'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolDashboard,
    outputSchema: DonationCampaignListResponseSchema,
  },
  getDonationsCampaignDetail: {
    operationName: toAppSyncOperationName('getDonationsCampaignDetail'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolDashboard,
    outputSchema: DonationCampaignSchema,
  },
} as const satisfies Record<string, DonationsQueryContract>);

// ---------------------------------------------------------------------------
// Mutation Contracts
// ---------------------------------------------------------------------------

export const DONATIONS_MUTATION_CONTRACTS = Object.freeze({
  createDonationsCampaign: {
    mutationName: toAppSyncMutationName('createDonationsCampaign'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolDashboard,
    inputSchema: DonationsCampaignMutationInputSchema,
    outputSchema: DonationCampaignSchema,
  },
  updateDonationsCampaign: {
    mutationName: toAppSyncMutationName('updateDonationsCampaign'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolDashboard,
    inputSchema: DonationsCampaignUpdateInputSchema,
    outputSchema: DonationCampaignSchema,
  },
  approveDonationsDedication: {
    mutationName: toAppSyncMutationName('approveDonationsDedication'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolModeration,
    inputSchema: DonationsDedicationActionInputSchema,
    outputSchema: DonationSchema,
  },
  rejectDonationsDedication: {
    mutationName: toAppSyncMutationName('rejectDonationsDedication'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolModeration,
    inputSchema: DonationsDedicationActionWithReasonInputSchema,
    outputSchema: DonationSchema,
  },
  createDonationsOfflineDonation: {
    mutationName: toAppSyncMutationName('createDonationsOfflineDonation'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolLedger,
    inputSchema: DonationSchema.partial(),
    outputSchema: DonationSchema,
  },
  issueDonationsRefund: {
    mutationName: toAppSyncMutationName('issueDonationsRefund'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolLedger,
    inputSchema: DonationsDedicationActionWithReasonInputSchema,
    outputSchema: DonationSchema,
  },
  resendDonationsReceipt: {
    mutationName: toAppSyncMutationName('resendDonationsReceipt'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolLedger,
    inputSchema: DonationsDedicationActionInputSchema,
    outputSchema: DonationsReceiptResultSchema,
  },
  updateDonationsTenantConfig: {
    mutationName: toAppSyncMutationName('updateDonationsTenantConfig'),
    routeId: DONATIONS_ROUTE_IDS.donationsSchoolDashboard,
    inputSchema: TenantParnasHayomConfigSchema.partial(),
    outputSchema: TenantParnasHayomConfigSchema,
  },
} as const satisfies Record<string, DonationsMutationContract>);

// ---------------------------------------------------------------------------
// Derived Types
// ---------------------------------------------------------------------------

export type DonationsQueryContractName = keyof typeof DONATIONS_QUERY_CONTRACTS;
export type DonationsMutationContractName = keyof typeof DONATIONS_MUTATION_CONTRACTS;
